import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { from, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CameraPermissionDialogComponent } from '../component/global-search/scanner/camera-permission-dialog/camera-permission-dialog.component';
import type { ScannerDialogResult } from '../component/global-search/scanner/scanner-dialog/scanner-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class DeviceScannerService {
  private _matDialog = inject(MatDialog);

  /**
   * Camera scanning needs `getUserMedia`, only exposed in a secure context (https).
   */
  public get isSupported() {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  }

  /**
   * Open the in-app scanner. When the browser is about to ask for the camera, an explanation dialog
   * is displayed first so its permission prompt is never shown without context. Once refused,
   * the scanner itself explains how to allow the camera again.
   * @returns the scanner result, or undefined when the user gave up.
   */
  public open(): Observable<ScannerDialogResult | undefined> {
    return from(this._getCameraPermissionState()).pipe(
      switchMap(permissionState =>
        permissionState === 'granted' || permissionState === 'denied'
          ? of(true)
          : this._openPermissionDialog()
      ),
      switchMap(accepted => (accepted ? this._openScannerDialog() : of(undefined)))
    );
  }

  private async _getCameraPermissionState(): Promise<PermissionState | 'unknown'> {
    try {
      // 'camera' is not part of the PermissionName typing, and not supported by every browser.
      const status = await navigator.permissions.query({ name: 'camera' as PermissionName });
      return status.state;
    } catch {
      return 'unknown';
    }
  }

  private _openPermissionDialog(): Observable<boolean> {
    return this._matDialog
      .open(CameraPermissionDialogComponent, { width: '360px', maxWidth: '90vw', autoFocus: false })
      .afterClosed()
      .pipe(map(accepted => !!accepted));
  }

  private _openScannerDialog(): Observable<ScannerDialogResult | undefined> {
    // Lazy loaded: the barcode decoding library is heavy and only needed here.
    return from(import('../component/global-search/scanner/scanner-dialog/scanner-dialog.component')).pipe(
      switchMap(({ ScannerDialogComponent }) =>
        this._matDialog
          .open<InstanceType<typeof ScannerDialogComponent>, void, ScannerDialogResult>(
            ScannerDialogComponent,
            {
              width: '100vw',
              height: '100dvh',
              maxWidth: '100vw',
              maxHeight: '100dvh',
              panelClass: 'scanner-dialog-panel',
              autoFocus: false,
            }
          )
          .afterClosed()
      )
    );
  }
}
